import React, { useState, useEffect } from 'react';
import {
  CreditCard, Plus, RefreshCw, Calendar, DollarSign, Pause, Play, Trash2,
  AlertCircle, CheckCircle, Clock, ChevronDown, ChevronUp, Settings
} from 'lucide-react';
import api from '../../services/api';
import PaymentAuthorizationModal from './PaymentAuthorizationModal';

function RecurringPaymentSchedules({ contactId, caseId = null }) {
  const [loading, setLoading] = useState(true);
  const [authorizations, setAuthorizations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [selectedAuth, setSelectedAuth] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('schedules');
  const [rejectingPlanId, setRejectingPlanId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    authorization_id: '',
    schedule_type: 'fixed_schedule',
    threshold_amount: '',
    replenish_amount: '',
    fixed_amount: '',
    frequency: 'monthly',
    billing_day: '1',
    create_invoice: false,
    invoice_description: ''
  });

  useEffect(() => {
    fetchData();
  }, [contactId, caseId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (contactId) params.append('contact_id', contactId);
      if (caseId) params.append('case_id', caseId);

      const [authRes, schedRes, txnRes] = await Promise.all([
        api.get(`/recurring-payments/authorizations?${params}`),
        api.get(`/recurring-payments/schedules?${params}`),
        api.get(`/recurring-payments/transactions?${params}&limit=20`)
      ]);

      setAuthorizations(authRes.data || []);
      setSchedules(schedRes.data || []);
      setTransactions(txnRes.data || []);
    } catch (err) {
      console.error('Failed to fetch recurring payment data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = (auth) => {
    fetchData();
    setSelectedAuth(auth);
    setShowScheduleForm(true);
    setScheduleForm(prev => ({ ...prev, authorization_id: auth.id }));
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    try {
      await api.post('/recurring-payments/schedules', {
        authorization_id: scheduleForm.authorization_id,
        schedule_type: scheduleForm.schedule_type,
        threshold_amount: scheduleForm.threshold_amount ? parseFloat(scheduleForm.threshold_amount) : null,
        replenish_amount: scheduleForm.replenish_amount ? parseFloat(scheduleForm.replenish_amount) : null,
        fixed_amount: scheduleForm.fixed_amount ? parseFloat(scheduleForm.fixed_amount) : null,
        frequency: scheduleForm.schedule_type === 'fixed_schedule' ? scheduleForm.frequency : null,
        billing_day: scheduleForm.billing_day ? parseInt(scheduleForm.billing_day) : null,
        create_invoice: scheduleForm.create_invoice,
        invoice_description: scheduleForm.invoice_description || null
      });
      setShowScheduleForm(false);
      setScheduleForm({
        authorization_id: '',
        schedule_type: 'fixed_schedule',
        threshold_amount: '',
        replenish_amount: '',
        fixed_amount: '',
        frequency: 'monthly',
        billing_day: '1',
        create_invoice: false,
        invoice_description: ''
      });
      fetchData();
    } catch (err) {
      console.error('Failed to create schedule:', err);
      alert(err.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handlePauseResume = async (schedule) => {
    try {
      if (schedule.status === 'active') {
        await api.post(`/recurring-payments/schedules/${schedule.id}/pause`);
      } else {
        await api.post(`/recurring-payments/schedules/${schedule.id}/resume`);
      }
      fetchData();
    } catch (err) {
      console.error('Failed to update schedule:', err);
    }
  };

  const handleCancelSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to cancel this recurring payment schedule?')) return;
    try {
      await api.delete(`/recurring-payments/schedules/${scheduleId}`);
      fetchData();
    } catch (err) {
      console.error('Failed to cancel schedule:', err);
    }
  };

  const handleRevokeAuth = async (authId) => {
    if (!window.confirm('Are you sure you want to revoke this payment authorization? This will also cancel any associated recurring schedules.')) return;
    try {
      await api.delete(`/recurring-payments/authorizations/${authId}`);
      fetchData();
    } catch (err) {
      console.error('Failed to revoke authorization:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      paused: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      completed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      pending: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      pending_approval: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      processing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      failed: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  if (loading) {
    return (
      <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="animate-spin text-virgo-teal-400 mr-2" size={20} />
          <span className="text-virgo-teal-200">Loading recurring payments...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700 shadow-lg">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h2 className="text-xl font-semibold text-virgo-teal-100 flex items-center">
          <CreditCard className="mr-2 text-virgo-teal-400" size={20} />
          Recurring Payments
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setShowAuthModal(true); }}
            className="px-3 py-1.5 bg-virgo-teal-600 text-white text-sm rounded-lg hover:bg-virgo-teal-500 transition flex items-center"
          >
            <Plus size={16} className="mr-1" />
            Add Card
          </button>
          <button className="text-virgo-teal-400 hover:text-virgo-teal-200">
            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Tabs */}
          <div className="flex gap-2 mt-4 border-b border-virgo-navy-600">
            <button
              onClick={() => setActiveTab('schedules')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                activeTab === 'schedules'
                  ? 'border-virgo-teal-400 text-virgo-teal-200'
                  : 'border-transparent text-virgo-teal-500 hover:text-virgo-teal-300'
              }`}
            >
              Schedules ({schedules.length})
            </button>
            <button
              onClick={() => setActiveTab('cards')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                activeTab === 'cards'
                  ? 'border-virgo-teal-400 text-virgo-teal-200'
                  : 'border-transparent text-virgo-teal-500 hover:text-virgo-teal-300'
              }`}
            >
              Cards ({authorizations.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                activeTab === 'history'
                  ? 'border-virgo-teal-400 text-virgo-teal-200'
                  : 'border-transparent text-virgo-teal-500 hover:text-virgo-teal-300'
              }`}
            >
              History ({transactions.length})
            </button>
          </div>

          {/* Schedules Tab */}
          {activeTab === 'schedules' && (
            <div className="mt-4">
              {schedules.length > 0 ? (
                <div className="space-y-3">
                  {schedules.map(schedule => (
                    <div key={schedule.id} className="bg-virgo-navy-700 rounded-lg p-4 border border-virgo-navy-600">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 text-xs rounded-full border ${getStatusColor(schedule.status)}`}>
                              {schedule.status.toUpperCase()}
                            </span>
                            <span className="text-xs text-virgo-teal-500">
                              {schedule.schedule_type === 'retainer_replenish' ? 'Retainer Replenishment' : 'Scheduled Payment'}
                            </span>
                          </div>
                          <div className="text-lg font-semibold text-virgo-teal-100">
                            {schedule.schedule_type === 'retainer_replenish'
                              ? `${formatCurrency(schedule.replenish_amount)} when balance < ${formatCurrency(schedule.threshold_amount)}`
                              : `${formatCurrency(schedule.fixed_amount)} ${schedule.frequency}`
                            }
                          </div>
                          <div className="text-sm text-virgo-teal-400 mt-1">
                            Card ending in {schedule.card_last_four || '****'}
                            {schedule.next_charge_date && (
                              <span className="ml-2">
                                <Clock size={12} className="inline mr-1" />
                                Next: {formatDate(schedule.next_charge_date)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {schedule.status === 'pending_approval' ? (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    await api.post(`/api/client-portal/staff/payments/plans/${schedule.id}/approve`);
                                    fetchData();
                                  } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
                                }}
                                className="px-3 py-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded text-sm"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => { setRejectingPlanId(schedule.id); setRejectReason(''); }}
                                className="px-3 py-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded text-sm"
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handlePauseResume(schedule)}
                                className="p-2 text-virgo-teal-400 hover:text-virgo-teal-200 hover:bg-virgo-navy-600 rounded"
                                title={schedule.status === 'active' ? 'Pause' : 'Resume'}
                              >
                                {schedule.status === 'active' ? <Pause size={16} /> : <Play size={16} />}
                              </button>
                              <button
                                onClick={() => handleCancelSchedule(schedule.id)}
                                className="p-2 text-red-400 hover:text-red-300 hover:bg-virgo-navy-600 rounded"
                                title="Cancel schedule"
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {rejectingPlanId === schedule.id && (
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            type="text"
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            placeholder="Rejection reason (optional)"
                            className="flex-1 px-3 py-1.5 bg-virgo-navy-800 border border-virgo-navy-500 rounded text-sm text-virgo-teal-100 placeholder-virgo-teal-600"
                            autoFocus
                          />
                          <button
                            onClick={async () => {
                              try {
                                await api.post(`/api/client-portal/staff/payments/plans/${schedule.id}/reject`, { reason: rejectReason });
                                setRejectingPlanId(null);
                                fetchData();
                              } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
                            }}
                            className="px-3 py-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded text-sm"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setRejectingPlanId(null)}
                            className="px-3 py-1.5 text-virgo-teal-400 hover:text-virgo-teal-200 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                      {schedule.proposal_notes && (
                        <div className="mt-1 text-xs text-virgo-teal-400 italic">
                          Client notes: {schedule.proposal_notes}
                        </div>
                      )}
                      <div className="mt-2 text-xs text-virgo-teal-500">
                        Total charged: {formatCurrency(schedule.total_charged)} ({schedule.charge_count} payments)
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-virgo-teal-500">
                  <Calendar className="mx-auto mb-3 opacity-50" size={40} />
                  <p>No recurring payment schedules</p>
                  {authorizations.length > 0 && (
                    <button
                      onClick={() => {
                        setScheduleForm(prev => ({ ...prev, authorization_id: authorizations[0].id }));
                        setShowScheduleForm(true);
                      }}
                      className="mt-3 px-4 py-2 bg-virgo-teal-600 text-white text-sm rounded-lg hover:bg-virgo-teal-500"
                    >
                      Create Schedule
                    </button>
                  )}
                </div>
              )}

              {/* Add Schedule Button */}
              {authorizations.length > 0 && schedules.length > 0 && (
                <button
                  onClick={() => {
                    setScheduleForm(prev => ({ ...prev, authorization_id: authorizations[0].id }));
                    setShowScheduleForm(true);
                  }}
                  className="mt-4 w-full py-2 border border-dashed border-virgo-navy-500 text-virgo-teal-400 rounded-lg hover:border-virgo-teal-500 hover:text-virgo-teal-300 transition flex items-center justify-center"
                >
                  <Plus size={16} className="mr-2" />
                  Add Another Schedule
                </button>
              )}
            </div>
          )}

          {/* Cards Tab */}
          {activeTab === 'cards' && (
            <div className="mt-4">
              {authorizations.length > 0 ? (
                <div className="space-y-3">
                  {authorizations.map(auth => (
                    <div key={auth.id} className="bg-virgo-navy-700 rounded-lg p-4 border border-virgo-navy-600 flex items-center justify-between">
                      <div className="flex items-center">
                        <CreditCard className="text-virgo-teal-400 mr-3" size={24} />
                        <div>
                          <div className="text-virgo-teal-100 font-medium">
                            {auth.card_brand?.toUpperCase() || 'Card'} ending in {auth.card_last_four}
                          </div>
                          <div className="text-sm text-virgo-teal-500">
                            Expires {auth.card_exp_month}/{auth.card_exp_year}
                            {auth.cardholder_name && <span className="ml-2">• {auth.cardholder_name}</span>}
                          </div>
                          <div className="text-xs text-virgo-teal-600 mt-1">
                            Authorized {formatDate(auth.authorized_at)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevokeAuth(auth.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-virgo-navy-600 rounded"
                        title="Revoke authorization"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-virgo-teal-500">
                  <CreditCard className="mx-auto mb-3 opacity-50" size={40} />
                  <p>No payment methods on file</p>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="mt-3 px-4 py-2 bg-virgo-teal-600 text-white text-sm rounded-lg hover:bg-virgo-teal-500"
                  >
                    Add a Card
                  </button>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="mt-4">
              {transactions.length > 0 ? (
                <div className="space-y-2">
                  {transactions.map(txn => (
                    <div key={txn.id} className="bg-virgo-navy-700 rounded-lg p-3 border border-virgo-navy-600 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                          txn.status === 'completed' ? 'bg-green-500/20' : txn.status === 'failed' ? 'bg-red-500/20' : 'bg-blue-500/20'
                        }`}>
                          {txn.status === 'completed' ? (
                            <CheckCircle className="text-green-400" size={16} />
                          ) : txn.status === 'failed' ? (
                            <AlertCircle className="text-red-400" size={16} />
                          ) : (
                            <Clock className="text-blue-400" size={16} />
                          )}
                        </div>
                        <div>
                          <div className="text-virgo-teal-100">
                            {formatCurrency(txn.amount)}
                            <span className="text-xs text-virgo-teal-500 ml-2">
                              {txn.trigger_type}
                            </span>
                          </div>
                          <div className="text-xs text-virgo-teal-500">
                            {formatDate(txn.processed_at || txn.created_at)}
                            {txn.failure_reason && (
                              <span className="text-red-400 ml-2">• {txn.failure_reason}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${getStatusColor(txn.status)}`}>
                        {txn.status.toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-virgo-teal-500">
                  <Clock className="mx-auto mb-3 opacity-50" size={40} />
                  <p>No payment history yet</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Authorization Modal */}
      <PaymentAuthorizationModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        contactId={contactId}
        caseId={caseId}
        onSuccess={handleAuthSuccess}
      />

      {/* Schedule Form Modal */}
      {showScheduleForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowScheduleForm(false)}>
          <div className="bg-virgo-navy-800 rounded-lg p-6 max-w-md w-full mx-4 border border-virgo-navy-600" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-virgo-teal-100 mb-4 flex items-center">
              <Settings className="mr-2 text-virgo-teal-400" size={20} />
              Create Payment Schedule
            </h3>

            <form onSubmit={handleCreateSchedule} className="space-y-4">
              {/* Card Selection */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Payment Card</label>
                <select
                  value={scheduleForm.authorization_id}
                  onChange={e => setScheduleForm(prev => ({ ...prev, authorization_id: e.target.value }))}
                  className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                  required
                >
                  <option value="">Select a card</option>
                  {authorizations.map(auth => (
                    <option key={auth.id} value={auth.id}>
                      {auth.card_brand?.toUpperCase()} ****{auth.card_last_four}
                    </option>
                  ))}
                </select>
              </div>

              {/* Schedule Type */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Schedule Type</label>
                <select
                  value={scheduleForm.schedule_type}
                  onChange={e => setScheduleForm(prev => ({ ...prev, schedule_type: e.target.value }))}
                  className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                >
                  <option value="fixed_schedule">Fixed Schedule (Weekly/Monthly)</option>
                  <option value="retainer_replenish">Retainer Replenishment (Threshold)</option>
                </select>
              </div>

              {scheduleForm.schedule_type === 'retainer_replenish' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Threshold Amount</label>
                    <input
                      type="number"
                      value={scheduleForm.threshold_amount}
                      onChange={e => setScheduleForm(prev => ({ ...prev, threshold_amount: e.target.value }))}
                      placeholder="Charge when balance falls below..."
                      className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Replenish Amount</label>
                    <input
                      type="number"
                      value={scheduleForm.replenish_amount}
                      onChange={e => setScheduleForm(prev => ({ ...prev, replenish_amount: e.target.value }))}
                      placeholder="Amount to charge"
                      className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Payment Amount</label>
                    <input
                      type="number"
                      value={scheduleForm.fixed_amount}
                      onChange={e => setScheduleForm(prev => ({ ...prev, fixed_amount: e.target.value }))}
                      placeholder="Amount to charge"
                      className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Frequency</label>
                    <select
                      value={scheduleForm.frequency}
                      onChange={e => setScheduleForm(prev => ({ ...prev, frequency: e.target.value }))}
                      className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Billing Day</label>
                    <input
                      type="number"
                      value={scheduleForm.billing_day}
                      onChange={e => setScheduleForm(prev => ({ ...prev, billing_day: e.target.value }))}
                      min="1"
                      max="28"
                      placeholder="Day of month (1-28)"
                      className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    />
                  </div>
                </>
              )}

              {/* Invoice Options */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="create_invoice"
                  checked={scheduleForm.create_invoice}
                  onChange={e => setScheduleForm(prev => ({ ...prev, create_invoice: e.target.checked }))}
                  className="w-4 h-4 rounded bg-virgo-navy-900 border-virgo-navy-500"
                />
                <label htmlFor="create_invoice" className="text-sm text-virgo-teal-300">
                  Create invoice for each payment
                </label>
              </div>

              {scheduleForm.create_invoice && (
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-300 mb-1">Invoice Description</label>
                  <input
                    type="text"
                    value={scheduleForm.invoice_description}
                    onChange={e => setScheduleForm(prev => ({ ...prev, invoice_description: e.target.value }))}
                    placeholder="e.g., Monthly Retainer Payment"
                    className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowScheduleForm(false)}
                  className="flex-1 py-2 border border-virgo-navy-500 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-500"
                >
                  Create Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecurringPaymentSchedules;
